import { consoleApiSlice } from "./consoleApiSlice";

export const consoleCustomerAnalyticsApiSlice = consoleApiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCustomerOverview: builder.query({
      query: ({ tiers } = {}) => ({
        url: `/customer-analytics/overview`,
        method: "GET",
        params: tiers && tiers.length ? { tiers: tiers.join(",") } : undefined,
      }),
      providesTags: ["CustomerOverview"],
    }),

    getLatestNps: builder.query({
      query: ({ websiteIds }) => ({
        url: `/customer-analytics/nps/latest?website_ids=${websiteIds.join(",")}`,
        method: "GET",
      }),
      providesTags: ["CustomerNps"],
    }),

    getNpsHistory: builder.query({
      query: ({ websiteId }) => ({
        url: `/customer-analytics/nps/history/${websiteId}`,
        method: "GET",
      }),
      providesTags: (result, error, { websiteId }) => [{ type: "CustomerNps", id: websiteId }],
    }),

    createNps: builder.mutation({
      query: (body) => ({
        url: `/customer-analytics/nps`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["CustomerNps", "CustomerOverview"],
    }),

    updateSubscriptionMeta: builder.mutation({
      query: ({ websiteId, renewalDate, starred }) => {
        const body = {};
        if (renewalDate !== undefined) body.renewal_date = renewalDate;
        if (starred !== undefined) body.starred = starred;
        return {
          url: `/customer-analytics/subscription-meta/${websiteId}`,
          method: "PUT",
          body,
        };
      },
      invalidatesTags: ["CustomerOverview"],
    }),

    getCustomerEventStats: builder.query({
      query: ({ timeRange, useProd = true } = {}) => ({
        url: `/customer-analytics/customer-events/stats`,
        method: "GET",
        params: {
          use_prod: useProd,
          ...(timeRange ? { range: timeRange } : {}),
        },
      }),
      providesTags: ["CustomerEvents"],
    }),

    getCustomerEvents: builder.query({
      query: ({ limit = 100, timeRange, useProd = true } = {}) => ({
        url: `/customer-analytics/customer-events?limit=${limit}`,
        method: "GET",
        params: {
          use_prod: useProd,
          ...(timeRange ? { range: timeRange } : {}),
        },
      }),
      providesTags: ["CustomerEvents"],
    }),

    // Pulls a deeper slice of the raw event list so sparse release_announcement.*
    // events survive the recency cap; filtered + aggregated client-side.
    getAnnouncementEvents: builder.query({
      query: ({ limit = 1000, timeRange, useProd = true } = {}) => ({
        url: `/customer-analytics/customer-events?limit=${limit}`,
        method: "GET",
        params: {
          use_prod: useProd,
          ...(timeRange ? { range: timeRange } : {}),
        },
      }),
      providesTags: ["CustomerEvents"],
    }),

    // Run-announcement notification sends (Platform notification_events rows,
    // payload stripped) — powers the Customer Actions notifications section.
    // Paged, searchable send history with per-send engagement (recipients' Hub visits
    // to the announced run). No range cap: omit start/end for the whole history.
    // Journey rollups (funnel, entry paths, top paths, surfaces, Activity feed, daily
    // actions, discards per agent type) for a UTC window; staff excluded upstream.
    getCustomerEngagement: builder.query({
      query: ({ start = null, end = null, websiteIds = [], useProd = true } = {}) => ({
        url: `/customer-analytics/engagement`,
        method: "GET",
        params: {
          use_prod: useProd,
          ...(start ? { start } : {}),
          ...(end ? { end } : {}),
          ...(websiteIds && websiteIds.length ? { website_id: websiteIds } : {}),
        },
      }),
      providesTags: ["CustomerEvents"],
    }),
    getNotificationSends: builder.query({
      query: ({ start = null, end = null, q = "", websiteId = null, state = null, limit = 50, offset = 0, useProd = true, engagementWindowDays = 14 } = {}) => ({
        url: `/customer-analytics/notification-sends`,
        method: "GET",
        params: {
          limit,
          offset,
          use_prod: useProd,
          engagement_window_days: engagementWindowDays,
          ...(start ? { start } : {}),
          ...(end ? { end } : {}),
          ...(q ? { q } : {}),
          ...(websiteId ? { website_id: websiteId } : {}),
          ...(state ? { state } : {}),
        },
      }),
      providesTags: ["CustomerEvents"],
    }),
    getNotificationEvents: builder.query({
      query: ({ limit = 500, timeRange, useProd = true, includeDryRun = false } = {}) => ({
        url: `/customer-analytics/notification-events`,
        method: "GET",
        params: {
          limit,
          use_prod: useProd,
          include_dry_run: includeDryRun,
          ...(timeRange ? { range: timeRange } : {}),
        },
      }),
      providesTags: ["CustomerEvents"],
    }),
  }),
});

export const {
  useGetCustomerOverviewQuery,
  useGetLatestNpsQuery,
  useGetNpsHistoryQuery,
  useCreateNpsMutation,
  useUpdateSubscriptionMetaMutation,
  useGetCustomerEventStatsQuery,
  useGetCustomerEventsQuery,
  useGetAnnouncementEventsQuery,
  useGetNotificationEventsQuery,
  useGetNotificationSendsQuery,
  useGetCustomerEngagementQuery,
} = consoleCustomerAnalyticsApiSlice;
